import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Shield } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import api from '../api';

const otpSchema = z.object({
  otp: z.string().min(6, 'OTP must be 6 digits').max(6, 'OTP must be 6 digits').regex(/^\d+$/, 'Only numbers allowed'),
});

const OTP = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [attempts, setAttempts] = useState(0);
  const [otpValues, setOtpValues] = useState(['', '', '', '', '', '']);
  const maxAttempts = 3;
  const navigate = useNavigate();
  const inputRefs = useRef([]);

  const { register, handleSubmit, setValue, formState: { errors } } = useForm({
    resolver: zodResolver(otpSchema),
  });

  useEffect(() => {
    inputRefs.current[0]?.focus();

    const token = localStorage.getItem('accessToken');
    const otpVerified = localStorage.getItem('otpVerified') === 'true';
    const postOtpRedirect = localStorage.getItem('postOtpRedirect') || '/main';

    if (!token) {
      navigate('/', { replace: true });
      return;
    }

    if (otpVerified) {
      navigate(postOtpRedirect, { replace: true });
      return;
    }

  }, [navigate]);

  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setInterval(() => {
        setResendCooldown((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [resendCooldown]);

  const handleResendOTP = async () => {
    if (resendCooldown > 0) return;
    setResendCooldown(30);
    try {
      const token = localStorage.getItem('accessToken'); // Changed from authToken to accessToken
      if (!token) {
        toast.error('No authentication token found. Please log in again.');
        navigate('/', { replace: true }); // Redirect to login page
        return;
      }
      const response = await api.post('/api/auth/resend-otp', {});
      if (response?.data?.accessToken) {
        localStorage.setItem('accessToken', response.data.accessToken);
      }
      setOtpValues(['', '', '', '', '', '']);
      setValue('otp', '');
      inputRefs.current[0]?.focus();
      toast.info('OTP resent to your mobile number.');
    } catch (err) {
      setResendCooldown(0);
      toast.error(err.response?.data?.message || 'Failed to resend OTP');
    }
  };

  const handleInputChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;
    const newOtpValues = [...otpValues];
    newOtpValues[index] = value;
    setOtpValues(newOtpValues);
    setValue('otp', newOtpValues.join(''));

    if (value && index < 5) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otpValues[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  const handlePaste = (e) => {
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    if (/^\d{6}$/.test(pastedData)) {
      const newOtpValues = pastedData.split('');
      setOtpValues(newOtpValues);
      setValue('otp', pastedData);
      inputRefs.current[5].focus();
    }
    e.preventDefault();
  };

  const onSubmit = async (data) => {
    if (attempts >= maxAttempts) {
      toast.error('Too many attempts. Please try again later.');
      return;
    }
    setIsLoading(true);
    try {
      const token = localStorage.getItem('accessToken'); // Changed from authToken to accessToken
      if (!token) {
        toast.error('No authentication token found. Please log in again.');
        navigate('/', { replace: true }); // Redirect to login page
        setIsLoading(false);
        return;
      }

      const response = await api.post('/api/auth/verify-otp', { otp: data.otp });

      // console.log('OTP verification response:', response.data);
  localStorage.setItem('otpVerified', 'true');
      setIsLoading(false);
        const postOtpRedirect = localStorage.getItem('postOtpRedirect') || '/main';
        toast.success('OTP verified successfully! Redirecting...');
      navigate(postOtpRedirect, { replace: true });
    } catch (err) {
      setIsLoading(false);
      setAttempts((prev) => prev + 1);
      // console.log('Full error response:', err.response);
      const errorMessage = err.response?.data?.message || 
        `OTP verification failed. ${maxAttempts - attempts - 1} attempts remaining.`;
      console.error('Error:', err.response?.status, errorMessage);
      toast.error(errorMessage);
    }
  };

  return (
    <div
      className="min-h-[100vh] w-full flex items-center justify-center bg-cover bg-center bg-no-repeat"
      style={{ backgroundImage: "url('/images/login-bg.jpg')" }}
    >
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm"></div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="relative z-10 w-full max-w-md mx-4"
      >
        <div className="bg-white/90 backdrop-blur-lg rounded-3xl shadow-2xl p-8 border border-white/20">
          <div className="text-center mb-8">
            <div className="bg-gradient-to-br from-green-600 to-green-700 p-3 rounded-2xl shadow-lg w-fit mx-auto mb-4">
              <Shield className="h-8 w-8 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-slate-900 font-['Inter'] bg-gradient-to-r from-green-600 to-green-700 bg-clip-text text-transparent">OTP Verification</h2>
            <p className="text-slate-600 mt-2">Enter the 6-digit code sent to your registered mobile number</p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">OTP Code</label>
              <div className="flex justify-center space-x-2">
                {otpValues.map((value, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: 0.3 + index * 0.07 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <input
                      ref={(el) => (inputRefs.current[index] = el)}
                      type="text"
                      value={value}
                      onChange={(e) => handleInputChange(index, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(index, e)}
                      onPaste={index === 0 ? handlePaste : undefined}
                      maxLength={1}
                      className="w-12 h-12 border border-slate-300 rounded-xl bg-white/80 backdrop-blur-sm focus:ring-2 focus:ring-green-500 focus:border-transparent text-center text-lg font-mono transition-all duration-150"
                      aria-label={`OTP digit ${index + 1}`}
                    />
                  </motion.div>
                ))}
              </div>
              <input
                {...register('otp')}
                type="hidden"
                value={otpValues.join('')}
              />
              {errors.otp && (
                <p id="otp-error" className="text-red-500 text-sm mt-2 text-center">
                  {errors.otp.message}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={isLoading || attempts >= maxAttempts}
              aria-disabled={isLoading || attempts >= maxAttempts}
              className="w-full bg-gradient-to-r from-green-600 to-green-700 text-white py-3 px-4 rounded-xl font-medium hover:from-green-700 hover:to-green-800 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              {isLoading ? (
                <div className="flex items-center justify-center space-x-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" aria-hidden="true"></div>
                  <span aria-live="polite">Verifying...</span>
                </div>
              ) : (
                'Verify OTP'
              )}
            </button>

          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-slate-600">
              Didn't receive the code?{' '}
              <button
                type="button"
                onClick={handleResendOTP}
                disabled={resendCooldown > 0}
                className={`text-green-600 font-medium transition-colors ${resendCooldown > 0 ? 'opacity-50 cursor-not-allowed' : 'hover:text-green-700'}`}
              >
                {resendCooldown > 0 ? `Resend OTP in ${resendCooldown}s` : 'Resend OTP'}
              </button>
            </p>
            <button
              type="button"
              onClick={() => {
                localStorage.removeItem('accessToken');
                localStorage.removeItem('refreshToken');
                localStorage.removeItem('otpVerified');
                navigate('/', { replace: true });
              }}
              className="mt-4 text-sm text-slate-500 hover:text-slate-700 transition-colors underline underline-offset-2"
            >
              &larr; Back to Login
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default OTP;